import React, {useCallback, useEffect, useMemo, useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, message, Table, Tabs} from 'antd';
import RouteFilter, {DEFAULT_FILTER_OPTIONS, FilterOptions} from './components/RouteFilter';
import RouteFormDrawer from './components/RouteFormDrawer';
import {gql, useQuery} from '@apollo/client';
import {
    GET_TMS_ROUTE_BY_ID,
    GET_TMS_ROUTE_LISTING,
    GetTmsRouteByIdResponse,
    TmsRouteFiltersInput
} from './graphql/queries/getTmsRoutes';
import {useTmsWaypointTimezones} from '../hooks/useTmsWaypointTimezones';
import {
    GET_TMS_CARRIERS_BY_WAREHOUSE_ID,
    GetTmsCarriersByWarehouseIdResponse
} from '../schedule/graphql/queries/getTmsCarriers';
import {
    GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID,
    GetHandoverCarriersByWarehouseIdResponse
} from './graphql/queries/getHandoverCarriers';
import {formatISODateTime} from "../../../utils/dateTimeUtils";
import {TmsBusinessTypeEnum, TmsCarrierSourceTypeEnum, TmsRouteTypeEnum, TmsStatusEnum, HandoverTypeEnum} from "../../../enums/TmsEnum";
import { DrawerMode } from './types/routeFormTypes';
import {useWarehouseContext} from '../../../contexts/WarehouseContext';
import {t} from '../../../i18n';
import TablePaginationBar from '../../../components/TablePaginationBar';

const GET_WAREHOUSES = gql`
    query GetWarehouses {
        warehouses: getWarehouseListForHeadquarters {
            id
            name
            sortingCode
            siteType
            parentSiteId
        }
    }
`;

const RoutePage: React.FC = () => {
    // 从 WarehouseContext 获取当前选中的 Hub（TmsLayout 提供）
    const { currentWarehouse } = useWarehouseContext();
    
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // 初始化时默认选中干线（routeType = 1）
    const [tempFilters, setTempFilters] = useState<FilterOptions>({
        ...DEFAULT_FILTER_OPTIONS,
        routeType: String(TmsRouteTypeEnum.Linehaul)
    });
    const [filters, setFilters] = useState<FilterOptions>({
        ...DEFAULT_FILTER_OPTIONS,
        routeType: String(TmsRouteTypeEnum.Linehaul)
    });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerKey, setDrawerKey] = useState(0);
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(DrawerMode.Create);
    const [selectedRoute, setSelectedRoute] = useState<any>(null);
    // 点击行时即算好抽屉是否展示途经点配置，避免详情/交接方数据异步到位前先展示后隐藏的闪烁
    const [drawerShowConfig, setDrawerShowConfig] = useState(true);

    // Query warehouses to get sorting code mapping
    const { data: warehousesData } = useQuery(GET_WAREHOUSES);

    // Query carriers for filter dropdown
    const { data: carriersData } = useQuery<GetTmsCarriersByWarehouseIdResponse>(GET_TMS_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : null
        },
        skip: !currentWarehouse?.id
    });

    // 查询交接方
    const { data: handoverCarriersData } = useQuery<GetHandoverCarriersByWarehouseIdResponse>(GET_HANDOVER_CARRIERS_BY_WAREHOUSE_ID, {
        variables: {
            warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0
        },
        skip: filters.routeType !== String(TmsRouteTypeEnum.Handover) || !currentWarehouse?.id
    });

    const handoverCarrierOptions = useMemo(() => {
        const list = handoverCarriersData?.getHandoverCarriersByWarehouseId ?? [];
        return list.map(hc => ({ label: hc.carrierName, value: hc.id }));
    }, [handoverCarriersData]);

    // Create warehouseOptions array for form drawer
    const warehouseOptions = useMemo(() => {
        if (!warehousesData?.warehouses) return [];
        return warehousesData.warehouses.map((w: {
            id: string;
            name?: string;
            sortingCode: string;
            parentSiteId: string;
            siteType: number;
        }) => ({
            id: w.id,
            sortingCode: w.sortingCode,
            parentSiteId: w.parentSiteId,
            siteType: w.siteType,
            name: w.name,
        }));
    }, [warehousesData]);

    const hubId = currentWarehouse?.id ? (typeof currentWarehouse.id === 'string' ? parseInt(currentWarehouse.id) : currentWarehouse.id) : undefined;

    // 构建查询过滤条件
    const queryFilters: TmsRouteFiltersInput = useMemo(() => {
        return {
            routeName: filters.routeName || undefined,
            routeType: filters.routeType ? Number(filters.routeType) : undefined,
            carrierId: filters.carrierId ? Number(filters.carrierId) : undefined,
            // 归属仓（当前 Hub）与用户所选起点分别下发，后端在 start_warehouse_id 列上 AND：
            // 用户选了非本仓起点时二者无交集，自然查不到别仓路线
            belongingWarehouseId: hubId,
            startWarehouseId: filters.startWarehouseId ? Number(filters.startWarehouseId) : undefined,
            endWarehouseId: filters.endWarehouseId ? Number(filters.endWarehouseId) : undefined,
            waypointWarehouseId: filters.waypointWarehouseId ? Number(filters.waypointWarehouseId) : undefined,
            status: filters.status ? Number(filters.status) : undefined
        };
    }, [filters, hubId]);

    // Query for route listing
    const { data, loading, refetch } = useQuery(GET_TMS_ROUTE_LISTING, {
        variables: {
            filter: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
            }
        },
        fetchPolicy: 'network-only',
        skip: !currentWarehouse?.id
    });

    const routeData = data?.getTmsRouteListing?.content || [];
    const totalElements = data?.getTmsRouteListing?.totalElements || 0;

    // 当 Hub 改变时，重置分页到第一页
    useEffect(() => {
        if (currentWarehouse?.id) {
            setCurrentPage(1);
        }
    }, [currentWarehouse?.id]);

    const statusOptions = useMemo(() => [
        { label: t('tms.route.status.active'), value: TmsStatusEnum.Active },
        { label: t('tms.route.status.inactive'), value: TmsStatusEnum.InActive }
    ], [t]);

    const routeTypeOptions = useMemo(() => [
        { label: t('tms.route.routeTypes.linehaul'), value: TmsRouteTypeEnum.Linehaul },
        { label: t('tms.route.routeTypes.feeder'), value: TmsRouteTypeEnum.Feeder },
        { label: t('tms.route.routeTypes.handover'), value: TmsRouteTypeEnum.Handover }
    ], [t]);

    const businessTypeOptions = useMemo(() => [
        { label: t('tms.route.businessTypes.ownRoute'), value: TmsBusinessTypeEnum.OwnRoute },
        { label: t('tms.route.businessTypes.temuGA'), value: TmsBusinessTypeEnum.TemuGA },
        { label: t('tms.route.businessTypes.temuSCF'), value: TmsBusinessTypeEnum.TemuSCF },
        { label: t('tms.route.businessTypes.tiktok'), value: TmsBusinessTypeEnum.TikTok },
    ], [t]);

    // 根据当前 filters.routeType 计算 Tab 激活项
    const activeRouteTypeTab = filters.routeType ?? String(TmsRouteTypeEnum.Linehaul);

    const handleRouteTypeTabChange = (key: string) => {
        // 切换 tab 时清空路线和承运商/交接方筛选条件
        const newFilters = {
            ...filters,
            routeType: key,
            routeName: '',
            carrierId: '',
        };
        setTempFilters(newFilters);
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const handleFilter = (newFilters: FilterOptions) => {
        setFilters({ ...newFilters });
        setCurrentPage(1);
    };

    const handleFilterChange = (newFilters: FilterOptions) => {
        setTempFilters({ ...newFilters });
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        if (size && size !== pageSize) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const { refetch: refetchRouteDetail } = useQuery<GetTmsRouteByIdResponse>(
        GET_TMS_ROUTE_BY_ID,
        {
            variables: { id: null },
            skip: true,
            fetchPolicy: 'no-cache',
        }
    );

    // 使用 Hook 获取 route waypoints
    const { fetchRouteWaypoints } = useTmsWaypointTimezones();

    const showEditDrawer = useCallback(async (record: any) => {
        // 点击即用列表 record 的 routeType/businessType/carrierSourceType 算好是否展示配置，作为 isShowConfig 传给抽屉，
        // 避免详情/交接方数据异步到位前按默认展示、返回后再隐藏造成的开场闪烁
        const isHandoverRoute = record.routeType === TmsRouteTypeEnum.Handover;
        const isLocalLastMileHandover = isHandoverRoute
            && record.carrierSourceType === HandoverTypeEnum.LocalLastMileCarrier;
        setDrawerShowConfig(!isLocalLastMileHandover);
        setSelectedRoute(null);
        setDrawerMode(DrawerMode.Edit);
        setDrawerVisible(true);

        try {
            const routeId = typeof record.id === 'string' ? parseInt(record.id) : record.id;

            
            const [routeResult, waypoints] = await Promise.all([
                refetchRouteDetail({ id: record.id }),
                fetchRouteWaypoints(routeId)
            ]);
            
            const routeDetail = routeResult.data?.getTmsRouteById;

            if (routeDetail) {
                setSelectedRoute({
                    ...routeDetail,
                    waypoints: waypoints
                });
            } else {
                message.error(t('tms.route.messages.routeDetailNotFound', { id: record.id }));
                setDrawerVisible(false);
            }
        } catch (error) {
            console.error('Failed to load route details:', error);
            message.error(t('tms.route.messages.failedToLoadRouteDetails'));
            setDrawerVisible(false);
        }
    }, [refetchRouteDetail, fetchRouteWaypoints]);
    const columns: TableColumnsType<any> = useMemo(() => [
        {
            title: t('tms.route.columns.route'),
            dataIndex: 'routeName',
            key: 'routeName',
            align: 'left' as const,
            ellipsis: false,
            render: (routeName: string, record: any) => (
                <Button
                    type="link"
                    onClick={() => showEditDrawer(record)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{routeName}</span>
                </Button>
            ),
        },
        {
            title: t('tms.route.columns.businessType'),
            dataIndex: 'businessType',
            key: 'businessType',
            align: 'left' as const,
            ellipsis: false,
            render: (businessType: number) => {
                const businessTypeOption = businessTypeOptions.find(option => option.value === businessType);
                const text = businessTypeOption?.label || '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        ...(activeRouteTypeTab !== String(TmsRouteTypeEnum.Handover) ? [{
            title: t('tms.route.columns.routeCode'),
            dataIndex: 'routeCode',
            key: 'routeCode',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>
        }] : []),
        {
            title: t('tms.route.columns.start'),
            dataIndex: 'startSortingCode',
            key: 'startSortingCode',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>,
        },
        {
            title: t('tms.route.columns.end'),
            dataIndex: 'endSortingCode',
            key: 'endSortingCode',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>,
        },
        {
            title: t('tms.route.columns.waypoint'),
            dataIndex: 'waypointSortingCodes',
            key: 'waypointSortingCodes',
            align: 'left' as const,
            ellipsis: false,
            render: (codes: string[]) => {
                const text = codes && codes.length > 0 ? codes.join(', ') : '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('tms.route.columns.plannedTotalHours'),
            dataIndex: 'plannedTotalHours',
            key: 'plannedTotalHours',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => <span style={{ whiteSpace: 'nowrap' }}>{value ?? '-'}</span>
        },
        {
            title: t('tms.route.columns.plannedTotalMiles'),
            dataIndex: 'plannedTotalMiles',
            key: 'plannedTotalMiles',
            align: 'left' as const,
            ellipsis: false,
            render: (value: number) => <span style={{ whiteSpace: 'nowrap' }}>{value ?? '-'}</span>
        },
        // carrierSourceType 列已在交接方需求中废弃，不再展示
        ...(activeRouteTypeTab === String(TmsRouteTypeEnum.Handover) ? [{
            title: t('tms.route.columns.handoverCarrierType'),
            dataIndex: 'carrierSourceType',
            key: 'carrierSourceType',
            align: 'left' as const,
            ellipsis: false,
            render: (carrierSourceType?: number | null) => {
                let text = '-';
                if (carrierSourceType === HandoverTypeEnum.LocalLastMileCarrier) {
                    text = t('tms.route.handoverTypes.localLastMileCarrier');
                } else if (carrierSourceType === HandoverTypeEnum.LinehaulCarrier) {
                    text = t('tms.route.handoverTypes.linehaulCarrier');
                }
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        }] : []),
        {
            title: activeRouteTypeTab === String(TmsRouteTypeEnum.Handover)
                ? t('tms.route.columns.handoverCarrier')
                : t('tms.route.columns.carrier'),
            dataIndex: 'carrierName',
            key: 'carrierName',
            align: 'left' as const,
            ellipsis: false,
            render: (carrierName: string) => <span style={{ whiteSpace: 'nowrap' }}>{carrierName || '-'}</span>
        },
        {
            title: t('tms.route.columns.status'),
            dataIndex: 'status',
            key: 'status',
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => {
                const statusOption = statusOptions.find(option => option.value === status);
                const text = statusOption?.label || t('tms.route.status.unknown');

                let color = '';
                switch (status) {
                    case 1:
                        color = 'text-green-600';
                        break;
                    case 0:
                        color = 'text-red-500';
                        break;
                    default:
                        color = 'text-gray-400';
                }

                return <span className={color} style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('tms.route.columns.updatedTime'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left' as const,
            ellipsis: false,
            render: (value) => {
                const formatted = formatISODateTime(value);
                return <span style={{ whiteSpace: 'nowrap' }}>{formatted}</span>;
            }
        },
        {
            title: t('tms.route.columns.action'),
            key: 'action',
            width: 120,
            align: 'left' as const,
            fixed: 'right',
            ellipsis: false,
            render: (_, record: any) => {
                return (
                    <div className="flex gap-2">
                        <Button
                            type="link"
                            onClick={() => showEditDrawer(record)}
                            style={{ paddingLeft: 0 }}
                        >
                            <span style={{ whiteSpace: 'nowrap' }}>{t('tms.route.buttons.edit')}</span>
                        </Button>
                    </div>
                );
            }
        }
    ], [businessTypeOptions, statusOptions, showEditDrawer, activeRouteTypeTab, t]);

    const showNewDrawer = () => {
        setDrawerShowConfig(true);
        setSelectedRoute(null);
        setDrawerMode(DrawerMode.Create);
        setDrawerVisible(true);
    };

    const handleDrawerSubmit = async () => {
        setDrawerVisible(false);
        setSelectedRoute(null);
        setDrawerKey(prev => prev + 1);
        // Trigger refetch to update the list while preserving filter state
        // Use network-only to ensure fresh data from server
        await refetch({
            filter: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
            }
        });
    };

    const handleApplyFilters = () => {
        handleFilter({ ...tempFilters });
    };

    const handleResetFilters = () => {
        const defaultRouteType = filters.routeType || String(TmsRouteTypeEnum.Linehaul);
        const resetFiltersValue: FilterOptions = {
            ...DEFAULT_FILTER_OPTIONS,
            routeType: defaultRouteType
        };
        setTempFilters(resetFiltersValue);
        handleFilter(resetFiltersValue);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* Filter Area */}
            <RouteFilter
                filters={tempFilters}
                onFilterChange={handleFilterChange}
                statusOptions={statusOptions}
                routeTypeOptions={routeTypeOptions}
                carriersData={carriersData?.getTmsCarriersByWarehouseId}
                handoverCarrierOptions={handoverCarrierOptions}
                warehouseOptions={warehouseOptions}
                currentRouteType={filters.routeType}
                isHandoverTab={activeRouteTypeTab === String(TmsRouteTypeEnum.Handover)}
            />
            
            {/* 批量操作区域 */}
            <div className="flex justify-between items-center mb-3 pl-0">
                {/* 左侧 - Route Type Tabs: 干线 / 支线 */}
                <div>
                    <Tabs
                        activeKey={activeRouteTypeTab}
                        onChange={handleRouteTypeTabChange}
                        items={[
                            {
                                key: String(TmsRouteTypeEnum.Linehaul),
                                label: t('tms.route.routeTypes.linehaul'),
                            },
                            {
                                key: String(TmsRouteTypeEnum.Feeder),
                                label: t('tms.route.routeTypes.feeder'),
                            },
                            {
                                key: String(TmsRouteTypeEnum.Handover),
                                label: t('tms.route.routeTypes.handover'),
                            },
                        ]}
                        className="[&_.ant-tabs-tab]:font-bold"
                    />
                </div>

                {/* 右侧 */}
                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        onClick={showNewDrawer}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('tms.route.buttons.new')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleApplyFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('tms.route.filters.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('tms.route.filters.reset')}</span>
                    </Button>
                </div>
            </div>

            {/* Table */}
            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={routeData}
                scroll={{ x: 'max-content' }}
                pagination={false}
                size="middle"
                tableLayout="auto"
             className="mb-4"/>

            {/* Pagination */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                onShowSizeChange={(_, size) => handlePageChange(1, size)}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            {/* Create/Edit Drawer */}
            <RouteFormDrawer
                key={drawerKey}
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleDrawerSubmit}
                initialValues={selectedRoute || undefined}
                mode={drawerMode}
                statusOptions={statusOptions}
                routeTypeOptions={routeTypeOptions}
                businessTypeOptions={businessTypeOptions}
                isHandoverTab={activeRouteTypeTab === String(TmsRouteTypeEnum.Handover)}
                isShowConfig={drawerShowConfig}
            />
        </div>
    );
};

export default RoutePage;

